import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ToastContent } from "../../../../shared/components";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { PermissionActionsEnum, PermissionRolesEnum } from "../../../../shared/enums/profiles";
import { checkByProfileGuard } from "../../../../shared/guards/ProfileGuard";
import { useDebounce, useToast } from "../../../../shared/hooks";
import { FireServices } from "../../../../shared/services/api/fire/FireServices";
import { ModalFireQuoteCancel } from "../../modals/ModalFireQuoteCancel";
import ModalFireQuoteDetails from "../../modals/ModalFireQuoteDetails";
import { ModalPDFViewer } from "../../modals/ModalPDFViewer";
import { FireQuotationData } from "../../Quotation/components/types/fire";
import { FireMenu } from "../FireMenu";
import { FireQuoteToolbar } from "../FireQuoteToolbar";
import type { FireTab } from "../TableFire";
import { TableFire } from "../TableFire";

// -------------------------------------------------------------
function getMonthDefaultRange() {
	const today = new Date();
	const ini = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split("T")[0];
	const fim = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split("T")[0];
	return { ini, fim };
}

// -------------------------------------------------------------
export const TabQuote = () => {
	const { debounce } = useDebounce();
	const toastHandler = useToast();
	const navigate = useNavigate();

	// Filtros
	const [textSearch, setTextSearch] = useState("");
	const [dataIni, setDataIni] = useState(getMonthDefaultRange().ini);
	const [dataFim, setDataFim] = useState(getMonthDefaultRange().fim);
	const [status, setStatus] = useState("todos");
	const [clientId, setClientId] = useState<string | null>(null);


	const [appliedFilters, setAppliedFilters] = useState({
		text: "",
		dataIni,
		dataFim,
		status: "todos",
		clientId: null as string | null,
	});


	// Tabela
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [totalCount, setTotalCount] = useState(0);
	const [data, setData] = useState<FireQuotationData[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [refreshTrigger, setRefreshTrigger] = useState(0);

	// Modal Detalhes
	const [modalState, setModalState] = useState({
		open: false,
		row: undefined as FireQuotationData | undefined,
		initialTab: "dados" as FireTab,
	});

	// PDF Viewer
	const [pdfModalOpen, setPdfModalOpen] = useState(false);
	const [pdfBase64, setPdfBase64] = useState<string | null>(null);

	// Cancelamento
	const [cancelModalOpen, setCancelModalOpen] = useState(false);
	const [cancelRow, setCancelRow] = useState<FireQuotationData | null>(null);

	// Menu
	const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
	const [selectedMenuRow, setSelectedMenuRow] = useState<FireQuotationData | null>(null);

	// ---------------------------------------------------------
	// Fetch principal (lista)
	// ---------------------------------------------------------
	useEffect(() => {
		setIsLoading(true);

		const doFetch = () => {
			FireServices.getFireQuotations(
				page + 1,
				rowsPerPage,
				appliedFilters.text,
				appliedFilters.status,
				appliedFilters.clientId ?? "",
				appliedFilters.dataIni,
				appliedFilters.dataFim
			)
				.then((response) => {
					if (response instanceof Error) {
						toastHandler.present({
							type: "error",
							position: "top-right",
							messageOrContent: (
								<ToastContent title="Erro" content="Erro ao buscar registros" />
							),
						});
						return;
					}

					setData(response.data);
					setTotalCount(response.totalCount ?? 0);
				})
				.finally(() => setIsLoading(false));
		};

		debounce(doFetch);
	}, [page, rowsPerPage, appliedFilters, refreshTrigger]);


	const handleSearch = () => {
		const raw = (textSearch ?? "").trim();

		let processedText = raw;

		// Se começa com 2 ou mais números → tratar como documento
		if (/^\d{2,}/.test(raw)) {
			processedText = raw.replace(/\D/g, "");
		}

		setAppliedFilters({
			text: processedText,
			dataIni,
			dataFim,
			status,
			clientId,
		});

		setPage(0);
	};

	const refreshList = () => setRefreshTrigger((prev) => prev + 1);

	// ---------------------------------------------------------
	// Ações
	// ---------------------------------------------------------
	const handleOpenFire = (id: string, tab?: FireTab) => {
		const row = data.find((r) => r.id === id);
		if (!row) return;

		setModalState({
			open: true,
			row,
			initialTab: tab ?? "dados",
		});
	};

	const handleCloseEdit = () => {
		setModalState({
			open: false,
			row: undefined,
			initialTab: "dados",
		});
	};

	// Menu
	const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, row: FireQuotationData) => {
		setMenuAnchorEl(event.currentTarget);
		setSelectedMenuRow(row);
	};

	const handleCloseMenu = () => {
		setMenuAnchorEl(null);
		setSelectedMenuRow(null);
	};

	// ---------------------------------------------------------
	// MENU ACTIONS
	// ---------------------------------------------------------
	const handleMenuAction = async (action: string, fireId: string) => {
		setIsLoading(true);

		// 1. Detalhes
		if (action === "details") {
			try {
				const resp = await FireServices.getFireQuoteByID(fireId);

				if (resp instanceof Error) {
					toastHandler.present({
						type: "error",
						position: "top-right",
						messageOrContent: <ToastContent title="Erro" content="Não foi possível carregar os detalhes." />,
					});
					return;
				}

				setModalState({
					open: true,
					row: resp,
					initialTab: "dados",
				});
			} finally {
				setIsLoading(false);
				handleCloseMenu();
			}
			return;
		}

		// 2. Editar
		if (action === "dados") {
			handleCloseMenu();
			setIsLoading(false);
			navigate(`/incendio/orcamento?id=${fireId}`);
			return;
		}

		// 3. PDF
		if (action === "downloadPDF") {
			try {
				const resp = await FireServices.firePrintQuotation(fireId);

				if (!resp || !resp.pdfBase64) throw new Error("PDF inválido.");

				setPdfBase64(resp.pdfBase64);
				setPdfModalOpen(true);

			} catch (err: any) {
				toastHandler.present({
					type: "error",
					position: "top-right",
					messageOrContent: <ToastContent title="Erro" content={err.message ?? "Falha ao abrir PDF"} />,
				});
			} finally {
				setIsLoading(false);
				handleCloseMenu();
			}
			return;
		}

		// 4. Cancelar → abre modal
		if (action === "cancel") {
			setCancelRow(selectedMenuRow);
			setCancelModalOpen(true);

			setIsLoading(false);
			handleCloseMenu();
			return;
		}
	};

	const handleMenuSelect = (action: string) => {
		if (selectedMenuRow) handleMenuAction(action, selectedMenuRow.id);
	};

	// ---------------------------------------------------------
	// CONFIRMAR CANCELAMENTO
	// ---------------------------------------------------------
	const handleConfirmCancel = async (id: string, reason: string) => {
		setIsLoading(true);

		try {
			const resp = await FireServices.FireCancelQuotation(id, reason);
			if (resp instanceof Error) throw new Error();

			toastHandler.present({
				type: "success",
				position: "top-right",
				messageOrContent: <ToastContent title="Sucesso" content="Orçamento cancelado!" />,
			});

			refreshList();
			setCancelModalOpen(false);
			setCancelRow(null);

		} catch {
			toastHandler.present({
				type: "error",
				position: "top-right",
				messageOrContent: <ToastContent title="Erro" content="Falha ao cancelar." />,
			});
		} finally {
			setIsLoading(false);
		}
	};

	// ---------------------------------------------------------
	// RENDER
	// ---------------------------------------------------------
	return (
		<Box sx={{ pt: 1 }}>
			<FireQuoteToolbar
				showDateRange
				showStatusSelect
				showNewQuoteButton={checkByProfileGuard(
					PermissionRolesEnum.SinisterMenu,
					PermissionActionsEnum.View
				)}
				textSearch={textSearch}
				onChangeTextSearch={setTextSearch}
				onClearTextSearch={() => setTextSearch("")}
				onChangeDateStart={setDataIni}
				onChangeDateEnd={setDataFim}
				onChangeStatus={setStatus}
				onClickSearch={handleSearch} // ou onClickRefresh, tanto faz
				onClickNewQuote={() => navigate("/incendio/orcamento")}
				showClientSelect
				onChangeClient={(id) => setClientId(id)}
			/>


			<LoadingComp isopen={isLoading} />

			<TableFire
				rows={data}
				totalCount={totalCount}
				page={page}
				rowsPerPage={rowsPerPage}
				onPageChange={setPage}
				onRowsPerPageChange={setRowsPerPage}
				onOpenFire={handleOpenFire}
				onOpenMenu={handleOpenMenu}
			/>

			<FireMenu
				anchorEl={menuAnchorEl}
				open={Boolean(menuAnchorEl)}
				onClose={handleCloseMenu}
				selectedRow={selectedMenuRow}
				onSelect={handleMenuSelect}
			/>

			{/* Detalhes */}
			<ModalFireQuoteDetails
				open={modalState.open}
				onClose={handleCloseEdit}
				data={modalState.row ?? null}
				isLoading={isLoading}
			/>

			{/* Cancelamento */}
			<ModalFireQuoteCancel
				open={cancelModalOpen}
				onClose={() => setCancelModalOpen(false)}
				data={cancelRow}
				onConfirm={handleConfirmCancel}
			/>

			{/* PDF */}
			<ModalPDFViewer
				open={pdfModalOpen}
				onClose={() => setPdfModalOpen(false)}
				base64={pdfBase64}
				filename={`orcamento-${modalState.row?.id}.pdf`}
			/>
		</Box>
	);
};
